import { Box, Button, Collapse, Stack, type BoxProps } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { LearningObjectAlternativeSection } from "./LearningObjectAlternativeSection.component";
import { LEVEL } from "../../consts";
import { LearningObjectAlternativeContext } from "../../context/LearningObjectAlternativeContext";
import type { ReferencedCourse, ReferencedLearningActivity } from "../../types";
import type { StructuredAlternativeSectionItem } from "../../utils/learningObjectStructure";
import { CourseActivityCard } from "../CourseActivityCard/CourseActivityCard.component";
import type { CourseActivityCardProps } from "../CourseActivityCard/CourseActivityCard.types";
import { ActivitySelectedProvider } from "../DetailsEsploraRestyle/IndexActivitySelected.context";
import { handleTeachersAndTutors } from "../LearningActiviyModal/utils";
import { PathCourseAccordion } from "../PathCourseAccordion/PathCourseAccordion.component";

type LearningObjectAlternativeFooterProps = Pick<CourseActivityCardProps,
  "canAccessNow" | "disabled" | "ecmRegistration" | "ecm_specialization" | "rootId" | "setOpenModalEcm"
> & {
  section: StructuredAlternativeSectionItem;
  sx?: BoxProps["sx"];
};

export function LearningObjectAlternativeFooter({
  canAccessNow,
  disabled,
  ecmRegistration,
  ecm_specialization,
  rootId,
  section,
  setOpenModalEcm,
  sx
}: LearningObjectAlternativeFooterProps) {
  const { t } = useTranslation();
  const [alternativesExpanded, setAlternativesExpanded] = useState(false);
  const alternativesId = useId();
  const alternativesButtonId = `${alternativesId}-button`;
  const unselectedAlternatives = section.alternatives.filter((item) => item.id !== section.selectedItem?.id);

  useEffect(() => {
    setAlternativesExpanded(false);
  }, [section.id, section.selectedItem?.id]);

  return (
    <Box sx={ [{ width: "100%" }, ...(Array.isArray(sx) ? sx : [sx])] }>
      <Box
        component="footer"
        sx={ {
          "& > .MuiButton-root": {
            fontSize: { sm: "0.875rem", xs: "0.75rem" },
            minWidth: 0
          },
          alignItems: "center",
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          width: "100%"
        } }
      >
        { /* Changing the selected alternative is independent of access to its learning content. */ }
        <LearningObjectAlternativeSection section={ section } />
        { unselectedAlternatives.length > 0 && (
          <Button
            aria-controls={ alternativesId }
            aria-expanded={ alternativesExpanded }
            color="primary"
            endIcon={ <ChevronDown
              aria-hidden="true"
              size="1rem"
              style={ {
                transform: alternativesExpanded ? "rotate(180deg)" : undefined,
                transition: "transform 200ms"
              } }
            /> }
            id={ alternativesButtonId }
            onClick={ (event) => {
              event.stopPropagation();
              setAlternativesExpanded((expanded) => !expanded);
            } }
            sx={ { marginLeft: "auto", textTransform: "none" } }
            variant="text"
          >
            { t("alternative_section_view_alternatives", { defaultValue: "Visualizza alternative" }) }
          </Button>
        ) }
      </Box>
      <Collapse
        aria-labelledby={ alternativesButtonId }
        id={ alternativesId }
        in={ alternativesExpanded && unselectedAlternatives.length > 0 }
        onClick={ (event) => event.stopPropagation() }
        role="region"
        sx={ { width: "100%" } }
        timeout={ 250 }
        unmountOnExit
      >
        <Stack gap={ 1.5 } paddingTop={ 2 }>
          <LearningObjectAlternativeContext.Provider value={ true }>
            <ActivitySelectedProvider syncUrlOnSelection={ false }>
              { unselectedAlternatives.map((alternative, index) => alternative.learningObjectTypology === LEVEL.COURSE ? (
                <PathCourseAccordion
                  course={ { ...alternative, isRequiredInStructure: false } as ReferencedCourse }
                  ecmRegistration={ ecmRegistration }
                  ecm_specialization={ ecm_specialization }
                  index={ index }
                  isAlternative
                  key={ alternative.id }
                  rootId={ rootId }
                  setOpenModalEcm={ setOpenModalEcm }
                />
              ) : (
                <CourseActivityCard
                  { ...alternative as ReferencedLearningActivity }
                  activity={ alternative as ReferencedLearningActivity }
                  alternativeSection={ undefined }
                  canAccessNow={ canAccessNow !== false && alternative.canAccessNow !== false }
                  disabled={ disabled || alternative.isEnable === false }
                  index={ index + 1 }
                  isAlternative
                  isRequiredInStructure={ false }
                  key={ alternative.id }
                  madeAttempts={ alternative.madeAttempts ?? undefined }
                  rootId={ (alternative as ReferencedLearningActivity).rootId || rootId }
                  setOpenModalEcm={ setOpenModalEcm }
                  teachers={ handleTeachersAndTutors(alternative.teachers ?? [], alternative.tutors ?? [], t) }
                />
              )) }
            </ActivitySelectedProvider>
          </LearningObjectAlternativeContext.Provider>
        </Stack>
      </Collapse>
    </Box>
  );
}
